import { useGetCompanyStyles } from './useGetCompanyStyles';

export interface CertificateAccessSettings {
  /** May this visitor be ASKED for their email? */
  collectEmail: boolean;
  /** May this visitor be ASKED for their location? */
  collectLocation: boolean;
  /**
   * Has the answer arrived?
   *
   * ⚠️ Callers must not collect, and must not render the gate, until this is true.
   * While it is false both flags read `false`, so treating it as "no" is already
   * the safe reading — this exists so a caller can tell "still asking" apart from
   * "asked, answer is no" and avoid flashing gated content in the gap.
   */
  isResolved: boolean;
}

/**
 * The `access` object `GET /certificates/public/:id` returns — the ALREADY-RESOLVED pair
 * for this one certificate, computed server-side down `certificate ?? product ?? company
 * ?? default`, per flag, independently (`resolveAccessSettings` in `@repo/types`).
 *
 * Typed here rather than imported because the public certificate payload has no
 * end-to-end type in this app: `getCertificatePublic` returns the raw response body.
 * Absent on a response the API's own resolution failed on, which is why it is optional
 * and why the company payload remains the fallback.
 */
export interface ResolvedCertificateAccess {
  collect_email?: boolean | null;
  collect_location?: boolean | null;
}

const UNRESOLVED: CertificateAccessSettings = {
  collectEmail: false,
  collectLocation: false,
  isResolved: false,
};

/**
 * The brand's Settings → Features → "Certificate access" toggles, for the public
 * certificate page.
 *
 * ## What this fixes
 *
 * `collect_email` and `collect_location` were WRITE-ONLY. A brand could switch
 * both off, see the platform save them, and still have every visitor asked for an
 * email and prompted for their location — the app consulted neither. The email
 * gate keyed off `viewType === PUBLIC && !isAccessDataCollected` and nothing else;
 * `ScanGeolocation` carried a `TODO(VERA-316 / VERA-315)` to gate on
 * `collect_location` that was never done. This is the read the toggles never had.
 *
 * ## The CERTIFICATE's resolved pair outranks the company's — VRG-591
 *
 * `collect_email` / `collect_location` are overridable per PRODUCT and per
 * CERTIFICATE (`null` = inherit), and the server already resolves the chain and
 * returns the answer as `access` on `GET /certificates/public/:id`. Pass it and it
 * wins outright.
 *
 * Reading the company row alone — which is all this hook did — meant an override a
 * brand set in the platform, and which the server ENFORCES on
 * `POST /certificates/public/:id/access`, was invisible to the client. The two
 * failure directions were both live and neither was silent-but-harmless: a
 * certificate-level opt-OUT still had every visitor asked, then 403'd on submit with
 * a generic "Please try again" and no way through; a certificate-level opt-IN under a
 * company that collects nothing had the visitor asked for nothing at all, so the
 * override collected nothing.
 *
 * It also removes a request from the critical path. The pair now arrives on the
 * certificate itself, so `isResolved` is true the moment the certificate lands
 * instead of one round trip later — the wait `CertificatePage` folds into its loading
 * state, which was real on localhost and on embeds where the boot-time company and
 * the certificate's company differ.
 *
 * ## Keyed on the CERTIFICATE's company, not the hostname
 *
 * `companyId` must come from the certificate being viewed
 * (`certificate.company_id`, which `GET /certificates/public/:id` already
 * returns), NOT from `CompanyContext`. The context id is resolved from the
 * subdomain and falls back to a hardcoded company when there is none — which is
 * every localhost page, every embed, and every brand that has moved onto its own
 * domain after takeover. Reading the flags off that id applies one brand's
 * privacy setting to another brand's visitor, which is the same class of bug as
 * not reading them at all.
 *
 * ## Costs no extra request in production
 *
 * `App.tsx` already fetches `useGetCompanyStyles` for the theme, keyed on the
 * company it resolved from the subdomain. On a brand's own subdomain that is the
 * same company as the certificate's, so this is a react-query cache hit. It
 * becomes a real request only where the two genuinely differ — which is exactly
 * where reusing the boot-time id would have been wrong.
 *
 * ## Fail direction: CLOSED, and the three states are not the same
 *
 * | `companyId` | meaning | result |
 * |---|---|---|
 * | `undefined` | the certificate has not loaded yet | unresolved — collect nothing, render nothing |
 * | `null` | loaded, and it has no company | resolved `{false, false}` — nothing to attribute consent to, so do not ask; the certificate still renders |
 * | a uuid | ask the API | its answer, or `{false, false}` if the payload has no `access` |
 *
 * A request FAILURE resolves to `{false, false}` rather than staying pending
 * forever, because a permanently-unresolved state would leave the page's content
 * region blank — trading a privacy fix for a broken page. Collecting data a brand
 * switched off is unrecoverable; not collecting is lost analytics. Take the
 * recoverable failure.
 *
 * The API distinguishes the same two cases on its side: a company that has never
 * configured the toggles gets `{collect_email: true, collect_location: true}`
 * explicitly (the schema default), and only a genuine lookup failure omits
 * `access` altogether. So a normal brand resolves TRUE here and collection is
 * unaffected — see `CertificateAccessSettingsService`.
 *
 * ## This is the PROMPT gate, not the control
 *
 * `POST /certificates/public/:id/access` and `PUT /scan/location` are public and
 * unauthenticated. Both re-resolve these settings server-side and refuse the
 * write themselves. Anything here is convenience for an honest browser.
 */
export const useCertificateAccessSettings = (
  companyId: string | null | undefined,
  certificateAccess?: ResolvedCertificateAccess | null,
): CertificateAccessSettings => {
  const { data, status } = useGetCompanyStyles({
    companyId: companyId ?? undefined,
  });

  // The certificate itself has not arrived, so we do not yet know whose settings
  // to read. Distinct from `null`, which is a resolved "it has no company".
  if (companyId === undefined) return UNRESOLVED;

  if (companyId === null) {
    return { collectEmail: false, collectLocation: false, isResolved: true };
  }

  /*
   * THE CERTIFICATE'S OWN ANSWER WINS. See the section above for why it has to.
   *
   * Both flags are read as `=== true`, so a `false` from the resolver is honoured as a
   * real answer rather than falling through to the company — which is the whole reason
   * an override exists. A payload carrying only one of the two still short-circuits:
   * `resolveAccessSettings` fills both or neither, so a half-present object is a shape
   * we do not have, and inventing a per-flag merge across two sources would silently
   * pair an override with a stale company value.
   */
  if (certificateAccess) {
    return {
      collectEmail: certificateAccess.collect_email === true,
      collectLocation: certificateAccess.collect_location === true,
      isResolved: true,
    };
  }

  if (status === 'pending') return UNRESOLVED;

  // `status === 'error'`, or a 2xx whose payload carries no `access` (an older
  // API, or one whose settings lookup failed). Both mean "unknown" — resolved so
  // the page renders, closed so nothing is collected.
  const access = status === 'success' ? data?.access : undefined;

  return {
    collectEmail: access?.collect_email === true,
    collectLocation: access?.collect_location === true,
    isResolved: true,
  };
};
